import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import { toAssetUrl } from '../../utils/assetBase';
import QrMenuV2 from './QrMenuV2';
import QrCover from './QrCover';
import QrCoverV2 from './QrCoverV2';
import QrAd from './QrAd';
import SeasonalLayer, { SeasonBanner, SEASON_THEMES, isSeason } from './SeasonalLayer';
import ImageLightbox from '../../components/ImageLightbox';
import WatermarkedImage from '../../components/WatermarkedImage';
import {
    House, ShoppingBag, Search, Plus, Minus, X, PencilLine, BellRing, Check, UtensilsCrossed, ArrowRight, ClipboardList,
} from 'lucide-react';


const formatMoney = (value) => `${Number(value || 0).toFixed(2)} ₼`;
const normalizeText = (value) => String(value || '').toLocaleLowerCase('az');

const getStepBySaleType = (item) => {
    if (item?.is_set) return 1;
    return item?.sale_type === 'piece' ? 1 : 0.1;
};
const formatQty = (qty, saleType) => (
    saleType === 'piece'
        ? String(Math.round(Number(qty || 0)))
        : Number(qty || 0).toFixed(1).replace(/\.0$/, '')
);

const getNameClass = (name) => {
    const len = String(name || '').length;
    if (len > 28) return 'text-sm';
    if (len > 20) return 'text-[15px]';
    return 'text-base';
};

// Cart key: composite for variants, plain itemId otherwise
const makeCartKey = (itemId, variantId = null) =>
    variantId ? `${itemId}_v${variantId}` : String(itemId);

// Sıralama üçün effektiv qiymət: variantlı məhsulda ən ucuz variant qiyməti
const effectivePrice = (item) => {
    const variants = item?.variants || [];
    if (variants.length > 0) {
        return Math.min(...variants.map((v) => Number(v.price || 0)));
    }
    return Number(item?.price || 0);
};

const QrMenuInner = ({ onSeason }) => {
    const [searchParams] = useSearchParams();
    const token = String(searchParams.get('t') || '').trim();

    const [tenant, setTenant] = useState(null);
    const [occupancy, setOccupancy] = useState(null); // {total, occupied, percent}
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState('all');
    // QR tema (superadmindən): --qr-primary = buttonlar/accent, --qr-bg = fon
    useEffect(() => {
        const th = tenant?.qr_theme || {};
        const root = document.documentElement;
        if (th.primary) root.style.setProperty('--qr-primary', th.primary); else root.style.removeProperty('--qr-primary');
        if (th.bg) { root.style.setProperty('--qr-bg', th.bg); document.body.style.background = th.bg; }
        else { root.style.removeProperty('--qr-bg'); document.body.style.background = ''; }
        return () => { root.style.removeProperty('--qr-primary'); root.style.removeProperty('--qr-bg'); document.body.style.background = ''; };
    }, [tenant]);
    // Superadmin mövsümi teması (Halloween / Yeni il / Novruz): bütün tenantlarda eyni anda
    const season = isSeason(tenant?.qr_season) ? tenant.qr_season : '';
    useEffect(() => {
        onSeason?.(season);
        const cl = document.documentElement.classList;
        if (season) cl.add('qr-season', `qr-season-${season}`);
        return () => cl.remove('qr-season', `qr-season-${season}`);
    }, [season]); // eslint-disable-line react-hooks/exhaustive-deps
    const [search, setSearch] = useState('');
    const [cart, setCart] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cartOpen, setCartOpen] = useState(false);
    const [noteModal, setNoteModal] = useState({ open: false, cartKey: null });
    const [noteDraft, setNoteDraft] = useState('');
    const [variantModal, setVariantModal] = useState({ open: false, item: null });
    const [zoomSrc, setZoomSrc] = useState(null); // şəkil böyütmə (lightbox)
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [customerNote, setCustomerNote] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [coverDismissed, setCoverDismissed] = useState(false);
    const [adDismissed, setAdDismissed] = useState(false);

    // Lock zoom on mobile — only for this QR menu page
    useEffect(() => {
        const meta = document.querySelector('meta[name=viewport]');
        const original = meta?.getAttribute('content') || '';
        meta?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
        return () => { if (original) meta?.setAttribute('content', original); };
    }, []);

    useEffect(() => {
        let active = true;

        const loadMenu = async () => {
            if (!token) {
                setError('QR menyu linki yanlışdır.');
                setLoading(false);
                return;
            }

            try {
                const res = await api.get(`/menu/public/full?token=${encodeURIComponent(token)}`);
                if (!active) return;

                const t = res.data?.tenant || null;
                // Önizləmə: linkə ?season=halloween | newyear | novruz əlavə etsən, superadmin seçimi olmadan da baxmaq olar
                const previewSeason = String(searchParams.get('season') || '').toLowerCase();
                if (t && isSeason(previewSeason)) {
                    t.qr_season = previewSeason;
                    t.qr_theme = { ...(t.qr_theme || {}), ...SEASON_THEMES[previewSeason] };
                }
                setTenant(t);
                setOccupancy(res.data?.occupancy || null);
                setCategories(res.data?.data || []);
                setActiveCategory('all');
            } catch (err) {
                if (!active) return;
                setError(err.response?.data?.msg || 'QR menyu yüklənmədi.');
            } finally {
                if (active) setLoading(false);
            }
        };

        loadMenu();
        return () => { active = false; };
    }, [token]);

    const groupedCategories = useMemo(() => {
        const query = normalizeText(search);
        return categories
            .map((category) => {
                // Sıra backend-dən gəlir (superadmin "Məhsul sıralaması" seçimi) — burada yenidən sıralamırıq
                const items = (category.items || [])
                    .filter((item) => {
                        if (!query) return true;
                        return normalizeText(item.name).includes(query)
                            || normalizeText(item.description).includes(query);
                    });
                return { ...category, items };
            })
            .filter((category) => category.items.length > 0);
    }, [categories, search]);

    // ── Scroll-spy navigasiya (sticky kateqoriya çubuğu) ─────────────────────
    const sectionRefs = useRef({});
    const chipRefs = useRef({});
    const chipScrollRef = useRef(null);
    const manualScrollRef = useRef(false);

    // Səhifə scroll edildikcə görünən kateqoriyanı yuxarıdakı çubuqda seç.
    // Scroll mövqeyindən birbaşa hesablanır (IntersectionObserver-dən etibarlı):
    // sticky çubuğun altından KEÇMİŞ ən aşağı bölmə = aktiv.
    useEffect(() => {
        if (loading || error) return;
        const onScroll = () => {
            if (manualScrollRef.current) return;
            const offset = 140; // yapışqan başlığın hündürlüyü + boşluq
            let current = 'all';
            for (const cat of groupedCategories) {
                const el = sectionRefs.current[String(cat.id)];
                if (!el) continue;
                if (el.getBoundingClientRect().top <= offset) current = String(cat.id);
                else break; // bölmələr sıra ilədir — bu artıq aşağıdadır
            }
            setActiveCategory((prev) => (prev === current ? prev : current));
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, [groupedCategories, loading, error]);

    // Aktiv chip-i çubuğun ORTASINA gətir — YALNIZ üfüqi (səhifəni şaquli tərpətmir).
    // scrollIntoView səhifəni də tərpədib "gedir-qayıdır" effekti yaradırdı.
    useEffect(() => {
        const btn = chipRefs.current[String(activeCategory)];
        const container = chipScrollRef.current;
        if (!btn || !container) return;
        const target = btn.offsetLeft - (container.clientWidth - btn.clientWidth) / 2;
        container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }, [activeCategory]);

    const scrollToCategory = (id) => {
        setActiveCategory(String(id));
        manualScrollRef.current = true;
        const el = sectionRefs.current[String(id)];
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => { manualScrollRef.current = false; }, 700);
    };

    const scrollToTop = () => {
        setActiveCategory('all');
        manualScrollRef.current = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => { manualScrollRef.current = false; }, 700);
    };

    const cartItems = useMemo(() => Object.values(cart), [cart]);

    const cartSummary = useMemo(() => {
        let itemCount = 0;
        let subtotal = 0;
        cartItems.forEach((entry) => {
            itemCount += Number(entry.qty || 0);
            subtotal += Number(entry.qty || 0) * Number(entry.price || 0);
        });
        const servicePercent = Number(tenant?.service_percent || 0);
        const serviceAmount = subtotal * (servicePercent / 100);
        const total = subtotal + serviceAmount;
        return { itemCount, subtotal, servicePercent, serviceAmount, total };
    }, [cartItems, tenant]);

    // ── Cart helpers ───────────────────────────────────────────────────────────

    const addToCart = (item, variant = null) => {
        // Vaxtı bitmiş set (QR-da qırmızı) sifariş oluna bilməz
        if (item.is_set && item.available_now === false) return;
        const cartKey = makeCartKey(item.id, variant?.id);
        const price = variant ? Number(variant.price) : Number(item.price);
        const step = getStepBySaleType(item);

        setCart((prev) => {
            const current = prev[cartKey];
            const currentQty = Number(current?.qty || 0);
            return {
                ...prev,
                [cartKey]: {
                    ...item,
                    _cartKey: cartKey,
                    _itemId: item.id,
                    _variantId: variant?.id || null,
                    _variantName: variant?.name || null,
                    price,
                    qty: currentQty + step,
                }
            };
        });
    };

    const updateCartQty = (cartKey, item, nextQty) => {
        const safeQty = Math.max(0, Number(nextQty || 0));
        setCart((prev) => {
            if (safeQty <= 0) {
                const next = { ...prev };
                delete next[cartKey];
                return next;
            }
            return { ...prev, [cartKey]: { ...prev[cartKey], qty: safeQty } };
        });
    };

    const increaseCartItem = (cartKey, item) => {
        const currentQty = Number(cart[cartKey]?.qty || 0);
        updateCartQty(cartKey, item, currentQty + getStepBySaleType(item));
    };

    const decreaseCartItem = (cartKey, item) => {
        const currentQty = Number(cart[cartKey]?.qty || 0);
        updateCartQty(cartKey, item, currentQty - getStepBySaleType(item));
    };

    // For items WITHOUT variants — inline stepper on item card
    const increaseItem = (item) => {
        const variants = item.variants || [];
        if (variants.length > 0) {
            setVariantModal({ open: true, item });
            return;
        }
        addToCart(item, null);
    };

    const decreaseItem = (item) => {
        // For no-variant items only
        const cartKey = makeCartKey(item.id, null);
        const currentQty = Number(cart[cartKey]?.qty || 0);
        updateCartQty(cartKey, item, currentQty - getStepBySaleType(item));
    };

    // How many of this menu item (all variants combined) are in cart
    const itemTotalQty = (itemId) =>
        cartItems
            .filter((ci) => ci._itemId === itemId || ci.id === itemId)
            .reduce((sum, ci) => sum + Number(ci.qty || 0), 0);

    const openNoteModal = (cartKey) => {
        setNoteDraft(String(cart[cartKey]?.note || ''));
        setNoteModal({ open: true, cartKey });
    };

    const saveNote = () => {
        if (!noteModal.cartKey || !cart[noteModal.cartKey]) {
            setNoteModal({ open: false, cartKey: null });
            setNoteDraft('');
            return;
        }
        setCart((prev) => ({
            ...prev,
            [noteModal.cartKey]: { ...prev[noteModal.cartKey], note: noteDraft.trim() }
        }));
        setNoteModal({ open: false, cartKey: null });
        setNoteDraft('');
    };

    // ── Submit order ───────────────────────────────────────────────────────────

    const submitOrder = async () => {
        if (cartItems.length === 0 || submitting) return;
        setSubmitError('');
        setSubmitting(true);
        try {
            const payload = {
                items: cartItems.map((ci) => ({
                    id: ci._itemId || ci.id,
                    set_id: ci.set_id || undefined,
                    qty: ci.qty,
                    price: ci.price,
                    name: ci.name || undefined,
                    note: ci.note || null,
                    _variantName: ci._variantName || null,
                    is_set: ci.is_set ? true : undefined,
                })),
                customer_note: customerNote.trim() || null,
            };
            await api.post(`/qr-orders/submit?token=${encodeURIComponent(token)}`, payload);
            setCart({});
            setCustomerNote('');
            setCartOpen(false);
            setSubmitted(true);
        } catch (err) {
            setSubmitError(err.response?.data?.msg || 'Sifariş göndərilmədi. Yenidən cəhd edin.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Item card ──────────────────────────────────────────────────────────────

    const renderItemCard = (item) => {
        const variants = item.variants || [];
        const hasVariants = variants.length > 0;
        const totalQty = itemTotalQty(item.id);

        // For no-variant items use simple cartKey
        const simpleKey = makeCartKey(item.id, null);
        const simpleQty = Number(cart[simpleKey]?.qty || 0);
        const unavailable = item.is_set && item.available_now === false;
        const qtyLabel = (q) => formatQty(q, item.is_set ? 'piece' : item.sale_type);

        // Şəklin küncündəki idarə: + / stepper / seçim
        const control = unavailable ? (
            <span className="rounded-full bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-400 shadow-md shadow-black/10 ring-1 ring-black/5">
                Vaxtı deyil
            </span>
        ) : hasVariants ? (
            <button
                type="button"
                onClick={() => setVariantModal({ open: true, item })}
                className="qr-brand-bg flex h-9 items-center gap-1 rounded-full pl-2.5 pr-3.5 text-xs font-semibold text-white shadow-md shadow-black/15 transition-transform active:scale-95"
            >
                <Plus className="h-4 w-4" strokeWidth={2.5} />
                {totalQty > 0 ? `${Math.round(totalQty)} seçilib` : 'Seç'}
            </button>
        ) : simpleQty > 0 ? (
            <div className="flex h-9 items-center rounded-full bg-white shadow-md shadow-black/15 ring-1 ring-black/5">
                <button
                    type="button"
                    onClick={() => decreaseItem(item)}
                    aria-label="Azalt"
                    className="qr-brand-text flex h-9 w-9 items-center justify-center rounded-full active:scale-90"
                >
                    <Minus className="h-4 w-4" strokeWidth={2.5} />
                </button>
                <span className="min-w-[20px] text-center text-sm font-bold text-gray-900">{qtyLabel(simpleQty)}</span>
                <button
                    type="button"
                    onClick={() => increaseItem(item)}
                    aria-label="Artır"
                    className="qr-brand-text flex h-9 w-9 items-center justify-center rounded-full active:scale-90"
                >
                    <Plus className="h-4 w-4" strokeWidth={2.5} />
                </button>
            </div>
        ) : (
            <button
                type="button"
                onClick={() => increaseItem(item)}
                aria-label="Əlavə et"
                className="qr-brand-bg flex h-9 w-9 items-center justify-center rounded-full text-white shadow-md shadow-black/15 transition-transform active:scale-90"
            >
                <Plus className="h-5 w-5" strokeWidth={2.5} />
            </button>
        );

        return (
            <article
                key={item.id}
                className={`flex gap-3 rounded-3xl bg-white p-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04] ${unavailable ? 'opacity-70' : ''}`}
            >
                <div className="flex min-w-0 flex-1 flex-col py-1 pl-1">
                    <h3 className={`${getNameClass(item.name)} break-words font-bold leading-snug text-gray-900`}>
                        {item.name}
                    </h3>

                    {Number(item.portion_pieces) > 1 && (
                        <div className="mt-1 self-start rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                            1 porsiya = {item.portion_pieces} ədəd
                        </div>
                    )}

                    {item.description && (
                        item.is_set ? (
                            <ul className="mt-1.5 space-y-0.5 text-xs leading-5 text-gray-500">
                                {item.description.split(',').map((part, idx) => {
                                    const t = part.trim();
                                    return t ? <li key={idx} className="flex gap-1.5"><span className="text-gray-300">•</span><span>{t}</span></li> : null;
                                })}
                            </ul>
                        ) : (
                            <p className="mt-1 line-clamp-2 text-xs leading-5 text-gray-500">{item.description}</p>
                        )
                    )}
                    {item.is_set && item.schedule_text && (
                        <p className={`mt-1.5 flex items-center gap-1.5 text-xs font-semibold ${item.available_now ? 'text-emerald-600' : 'text-red-500'}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${item.available_now ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            {item.available_now ? `Aktivdir · ${item.schedule_text}` : item.schedule_text}
                        </p>
                    )}

                    {/* Variant siyahısı: qiymətlər kartda görünsün (sifariş bağlı olsa belə) */}
                    {hasVariants && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                            {variants.map((v) => (
                                <span key={v.id} className="rounded-lg bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600 ring-1 ring-black/[0.04]">
                                    {v.name} <span className="qr-brand-text font-semibold">{formatMoney(v.price)}</span>
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="mt-auto flex flex-wrap items-center gap-2 pt-2.5">
                        {hasVariants ? (
                            <span className="text-xs font-medium text-gray-400">{variants.length} seçim</span>
                        ) : (
                            <span className="qr-brand-text text-[15px] font-bold">{formatMoney(item.price)}</span>
                        )}
                        {!hasVariants && simpleQty > 0 && (
                            <button
                                type="button"
                                onClick={() => openNoteModal(simpleKey)}
                                className="flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 active:bg-gray-200"
                            >
                                <PencilLine className="h-3 w-3" />
                                {cart[simpleKey]?.note ? 'Qeydi dəyiş' : 'Qeyd'}
                            </button>
                        )}
                    </div>

                    {!hasVariants && cart[simpleKey]?.note && (
                        <div className="mt-2 rounded-xl bg-amber-50 px-2.5 py-1.5 text-[11px] leading-4 text-amber-800">
                            {cart[simpleKey].note}
                        </div>
                    )}
                </div>

                <div className="relative flex-shrink-0 self-start">
                    {item.image_url ? (
                        <div className="h-28 w-28 overflow-hidden rounded-2xl bg-gray-100 sm:h-32 sm:w-32">
                            <WatermarkedImage
                                src={toAssetUrl(item.image_url)}
                                alt={item.name}
                                onClick={() => setZoomSrc(toAssetUrl(item.image_url))}
                                fontSize={12}
                            />
                        </div>
                    ) : (
                        <div className="qr-soft flex h-28 w-28 items-center justify-center rounded-2xl sm:h-32 sm:w-32">
                            <UtensilsCrossed className="qr-brand-text h-8 w-8 opacity-50" />
                        </div>
                    )}
                    <div className="absolute -bottom-1.5 right-1.5">{control}</div>
                </div>
            </article>
        );
    };

    // ── Reklam ekranı — cover-dən əvvəl 5 sn göstərilir ────────────────────────

    if (!loading && !error && tenant && Number(tenant.qr_ad_enabled || 0) && tenant.qr_ad_image_url && !adDismissed && !submitted) {
        return <QrAd imageUrl={tenant.qr_ad_image_url} onSkip={() => setAdDismissed(true)} />;
    }

    // ── Cover (welcome) ekranı — V1 və V2 hər ikisi üçün giriş qapısı ──────────

    if (!loading && !error && tenant && !coverDismissed && !submitted) {
        // Giriş ekranı dizaynı superadmindən. Standart V2 (modern); yalnız açıq 'v1' seçilibsə klassik.
        // Önizləmə: linkə ?cover=v2 (və ya v1) əlavə etsən, seçmədən baxmaq olar.
        const coverParam = String(searchParams.get('cover') || '').toLowerCase();
        const coverDesign = ['v1', 'v2'].includes(coverParam) ? coverParam : tenant?.qr_theme?.cover_design;
        const Cover = coverDesign === 'v1' ? QrCover : QrCoverV2;
        return <Cover tenant={tenant} token={token} occupancy={occupancy} onEnter={() => setCoverDismissed(true)} />;
    }

    // ── Design switch: V2 render olur, V1 (aşağıdakı) default qalır ────────────

    if (!loading && !error && tenant?.qr_menu_design === 'v2') {
        return <QrMenuV2 token={token} season={season} onHome={() => setCoverDismissed(false)} />;
    }

    // ── Success screen ─────────────────────────────────────────────────────────

    if (submitted) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center px-5 text-center" style={{ touchAction: 'manipulation', background: 'var(--qr-bg, #f5f7f9)' }}>
                <div className="w-full max-w-sm rounded-[32px] bg-white p-8 shadow-xl shadow-black/[0.06] ring-1 ring-black/[0.04]">
                    <div className="qr-soft mx-auto flex h-20 w-20 items-center justify-center rounded-full">
                        <div className="qr-brand-bg flex h-14 w-14 items-center justify-center rounded-full text-white">
                            <Check className="h-7 w-7" strokeWidth={3} />
                        </div>
                    </div>
                    <h2 className="mt-6 text-2xl font-bold tracking-tight text-gray-900">Sifariş qəbul edildi!</h2>
                    <p className="mt-2 text-sm leading-relaxed text-gray-500">
                        Sifarişiniz ofisianta göndərildi. Tezliklə sizə yardımçı olacaq.
                    </p>
                    <button
                        type="button"
                        onClick={() => setSubmitted(false)}
                        className="qr-brand-bg mt-7 w-full rounded-2xl py-3.5 text-sm font-semibold text-white"
                    >
                        Menyuya qayıt
                    </button>
                </div>
                <div className="qr-on-bg-muted mt-6 text-xs text-gray-400">
                    Powered by{' '}
                    <a href="https://www.fiscalbusiness.net" target="_blank" rel="noopener noreferrer" className="qr-brand-text font-semibold">
                        fiscalbusiness.net
                    </a>
                </div>
            </div>
        );
    }

    // ── Main render ────────────────────────────────────────────────────────────

    const orderingOn = Number(tenant?.qr_menu_ordering_enabled ?? 1) === 1;

    return (
        <div className="min-h-screen text-gray-900" style={{ touchAction: 'manipulation', background: 'var(--qr-bg, #f5f7f9)' }}>
            {/* ── Yapışqan başlıq: ad + səbət + kateqoriyalar ── */}
            <header className="sticky top-0 z-30 border-b border-black/[0.05] bg-white/90 backdrop-blur-xl">
                <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 pb-2.5 pt-3">
                    <button
                        type="button"
                        onClick={() => setCoverDismissed(false)}
                        title="Əsas səhifəyə qayıt"
                        aria-label="Əsas səhifə"
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 active:bg-gray-200"
                    >
                        <House className="h-[18px] w-[18px]" />
                    </button>
                    <div className="min-w-0 flex-1">
                        <h1 className="truncate text-[17px] font-bold leading-tight tracking-tight text-gray-900">{tenant?.name || 'Restoran menyusu'}</h1>
                        {occupancy && occupancy.total > 0 ? (
                            <div className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
                                <span className={`h-1.5 w-1.5 rounded-full ${
                                    occupancy.percent >= 70 ? 'bg-rose-500' : occupancy.percent >= 40 ? 'bg-amber-500' : 'bg-emerald-500'
                                }`} />
                                <span className="truncate">{occupancy.total} masadan {occupancy.occupied} dolu · {occupancy.percent}%</span>
                            </div>
                        ) : (
                            <div className="mt-0.5 text-[11px] font-medium text-gray-400">Menyu</div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setCartOpen(true)}
                        aria-label="Səbət"
                        className="qr-brand-bg relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-white shadow-sm active:scale-95"
                    >
                        <ShoppingBag className="h-[18px] w-[18px]" />
                        {cartSummary.itemCount > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white ring-2 ring-white">
                                {Math.round(cartSummary.itemCount)}
                            </span>
                        )}
                    </button>
                </div>

                {!loading && !error && groupedCategories.length > 0 && (
                    <div ref={chipScrollRef} className="qr-noscroll mx-auto flex max-w-5xl gap-1.5 overflow-x-auto px-4 pb-3">
                        <button
                            type="button"
                            ref={(el) => { chipRefs.current['all'] = el; }}
                            onClick={scrollToTop}
                            className={`flex-shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
                                activeCategory === 'all' ? 'qr-brand-bg text-white shadow-sm' : 'bg-gray-100 text-gray-600'
                            }`}
                        >
                            Hamısı
                        </button>
                        {groupedCategories.map((category) => (
                            <button
                                key={category.id}
                                type="button"
                                ref={(el) => { chipRefs.current[String(category.id)] = el; }}
                                onClick={() => scrollToCategory(category.id)}
                                className={`flex-shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
                                    String(activeCategory) === String(category.id) ? 'qr-brand-bg text-white shadow-sm' : 'bg-gray-100 text-gray-600'
                                }`}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>
                )}
            </header>

            <main className="mx-auto w-full max-w-5xl px-4 pb-32 pt-4">
                <SeasonBanner season={season} />
                {/* Axtarış */}
                <div className="relative">
                    <Search className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Məhsul axtar"
                        className="w-full rounded-2xl border-0 bg-white py-3.5 pl-11 pr-11 text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(16,24,40,0.04)] outline-none ring-1 ring-black/[0.05] transition placeholder:text-gray-400 focus:ring-2 focus:ring-[color:var(--qr-primary,#0090bb)]"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            aria-label="Təmizlə"
                            className="absolute right-2.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-gray-100 text-gray-500"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>

                {Number(tenant?.service_percent || 0) > 0 && (
                    <div className="qr-soft mt-3 rounded-2xl px-4 py-2.5 text-xs font-medium text-gray-600">
                        Hesaba <b className="qr-brand-text">{Number(tenant?.service_percent || 0).toFixed(0)}%</b> xidmət haqqı əlavə olunur
                    </div>
                )}

                {loading ? (
                    <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="flex h-[136px] animate-pulse gap-3 rounded-3xl bg-white p-3 ring-1 ring-black/[0.04]">
                                <div className="flex-1 space-y-2 py-1">
                                    <div className="h-4 w-2/3 rounded bg-gray-100" />
                                    <div className="h-3 w-full rounded bg-gray-100" />
                                    <div className="h-3 w-1/3 rounded bg-gray-100" />
                                </div>
                                <div className="h-28 w-28 rounded-2xl bg-gray-100" />
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="mt-6 rounded-3xl bg-white p-10 text-center text-sm font-medium text-red-500 ring-1 ring-black/[0.04]">{error}</div>
                ) : groupedCategories.length === 0 ? (
                    <div className="mt-6 flex flex-col items-center rounded-3xl bg-white p-10 text-center ring-1 ring-black/[0.04]">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                            <Search className="h-5 w-5" />
                        </div>
                        <div className="mt-3 text-sm font-semibold text-gray-700">Məhsul tapılmadı</div>
                        <div className="mt-0.5 text-xs text-gray-400">Başqa söz ilə axtarın.</div>
                    </div>
                ) : (
                    <div className="mt-6 space-y-8">
                        {groupedCategories.map((category) => (
                            <section
                                key={category.id}
                                ref={(el) => { sectionRefs.current[String(category.id)] = el; }}
                                data-cat-id={String(category.id)}
                                className="scroll-mt-[124px]"
                            >
                                <div className="mb-3 flex items-baseline justify-between gap-3 px-1">
                                    <h2 className="qr-on-bg text-xl font-bold tracking-tight text-gray-900">{category.name}</h2>
                                    <span className="qr-on-bg-muted flex-shrink-0 text-xs font-medium text-gray-400">{category.items.length} məhsul</span>
                                </div>
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                    {category.items.map((item) => renderItemCard(item))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}

                <div className="qr-on-bg-muted mt-10 text-center text-xs text-gray-400">
                    Powered by{' '}
                    <a href="https://www.fiscalbusiness.net" target="_blank" rel="noopener noreferrer" className="qr-brand-text font-semibold">
                        fiscalbusiness.net
                    </a>
                </div>
            </main>

            {/* ── Üzən səbət paneli: yalnız məhsul varsa ── */}
            {cartSummary.itemCount > 0 && !cartOpen && (
                <div className="fixed inset-x-0 bottom-0 z-30 px-3" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
                    <button
                        type="button"
                        onClick={() => setCartOpen(true)}
                        className="qr-brand-bg mx-auto flex w-full max-w-md items-center gap-3 rounded-2xl px-3.5 py-3 text-white shadow-xl shadow-black/25 transition-transform active:scale-[0.98]"
                    >
                        <span className="flex h-9 min-w-9 items-center justify-center rounded-xl bg-white/20 px-2 text-sm font-bold">
                            {Math.round(cartSummary.itemCount)}
                        </span>
                        <span className="min-w-0 flex-1 text-left">
                            <span className="block text-[11px] font-medium text-white/75">
                                {`${formatQty(cartSummary.itemCount, 'piece')} məhsul`}
                                {cartSummary.servicePercent > 0 && ` · xidmət ${formatMoney(cartSummary.serviceAmount)}`}
                            </span>
                            <span className="block text-base font-bold leading-tight">{formatMoney(cartSummary.total)}</span>
                        </span>
                        <span className="flex flex-shrink-0 items-center gap-1 rounded-xl bg-white/15 px-3 py-2 text-sm font-semibold">
                            Səbət <ArrowRight className="h-4 w-4" />
                        </span>
                    </button>
                </div>
            )}

            {/* ── Səbət (aşağıdan açılan) ── */}
            {cartOpen && (
                <div
                    className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 backdrop-blur-[2px] sm:items-center sm:p-4"
                    onClick={(e) => e.target === e.currentTarget && setCartOpen(false)}
                >
                    <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:rounded-[28px]">
                        <div className="mx-auto mt-2.5 h-1.5 w-10 flex-shrink-0 rounded-full bg-gray-200 sm:hidden" />
                        <div className="flex flex-shrink-0 items-center justify-between gap-3 px-5 pb-3 pt-3">
                            <div>
                                <h3 className="text-xl font-bold tracking-tight text-gray-900">Səbət</h3>
                                <p className="text-xs text-gray-400">{cartItems.length ? `${formatQty(cartSummary.itemCount, 'piece')} məhsul` : 'Boşdur'}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setCartOpen(false)}
                                aria-label="Bağla"
                                className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600 active:bg-gray-200"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-4 pb-4">
                            {cartItems.length === 0 ? (
                                <div className="flex flex-col items-center rounded-3xl border border-dashed border-gray-200 px-6 py-10 text-center">
                                    <div className="qr-soft flex h-14 w-14 items-center justify-center rounded-full">
                                        <ShoppingBag className="qr-brand-text h-6 w-6" />
                                    </div>
                                    <div className="mt-3 text-sm font-semibold text-gray-700">Səbət boşdur</div>
                                    <div className="mt-0.5 text-xs text-gray-400">Menyudan məhsul əlavə edin.</div>
                                </div>
                            ) : (
                                cartItems.map((item) => {
                                    const cartKey = item._cartKey || String(item.id);
                                    return (
                                        <div key={cartKey} className="rounded-2xl bg-gray-50 p-3 ring-1 ring-black/[0.03]">
                                            <div className="flex gap-3">
                                                <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl bg-white">
                                                    {item.image_url ? (
                                                        <img src={toAssetUrl(item.image_url)} alt="" draggable={false} className="h-full w-full object-cover" />
                                                    ) : (
                                                        <div className="qr-soft flex h-full w-full items-center justify-center">
                                                            <UtensilsCrossed className="qr-brand-text h-5 w-5 opacity-50" />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-semibold leading-snug text-gray-900">{item.name}</div>
                                                            {item._variantName && (
                                                                <div className="qr-brand-text mt-0.5 text-xs font-medium">{item._variantName}</div>
                                                            )}
                                                        </div>
                                                        <div className="flex-shrink-0 text-sm font-bold text-gray-900">
                                                            {formatMoney(Number(item.qty || 0) * Number(item.price || 0))}
                                                        </div>
                                                    </div>
                                                    <div className="mt-0.5 text-xs text-gray-400">{formatMoney(item.price)} / ədəd</div>
                                                </div>
                                            </div>

                                            <div className="mt-2.5 flex items-center justify-between gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => openNoteModal(cartKey)}
                                                    className="flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-gray-600 ring-1 ring-black/[0.06]"
                                                >
                                                    <PencilLine className="h-3.5 w-3.5" />
                                                    {item.note ? 'Qeydi dəyiş' : 'Qeyd əlavə et'}
                                                </button>
                                                <div className="flex h-9 items-center rounded-full bg-white ring-1 ring-black/[0.06]">
                                                    <button
                                                        type="button"
                                                        onClick={() => decreaseCartItem(cartKey, item)}
                                                        aria-label="Azalt"
                                                        className="qr-brand-text flex h-9 w-9 items-center justify-center rounded-full active:scale-90"
                                                    >
                                                        <Minus className="h-4 w-4" strokeWidth={2.5} />
                                                    </button>
                                                    <span className="min-w-[28px] text-center text-sm font-bold text-gray-900">
                                                        {formatQty(item.qty, item.is_set ? 'piece' : item.sale_type)}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => increaseCartItem(cartKey, item)}
                                                        aria-label="Artır"
                                                        className="qr-brand-text flex h-9 w-9 items-center justify-center rounded-full active:scale-90"
                                                    >
                                                        <Plus className="h-4 w-4" strokeWidth={2.5} />
                                                    </button>
                                                </div>
                                            </div>

                                            {item.note && (
                                                <div className="mt-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                                    {item.note}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}

                            {/* Ümumi qeyd */}
                            {cartItems.length > 0 && (
                                <div className="pt-1">
                                    <label className="mb-1.5 block px-1 text-xs font-medium text-gray-500">Ofisianta ümumi qeyd</label>
                                    <textarea
                                        value={customerNote}
                                        onChange={(e) => setCustomerNote(e.target.value)}
                                        rows={2}
                                        placeholder="Məs: pəncərə yanında oturmaq istəyirik..."
                                        className="w-full resize-none rounded-2xl border-0 bg-gray-50 px-4 py-3 text-sm outline-none ring-1 ring-black/[0.05] transition placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-[color:var(--qr-primary,#0090bb)]"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Cəm + göndər */}
                        <div className="flex-shrink-0 border-t border-gray-100 bg-white px-5 pt-4" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
                            <div className="space-y-1.5 text-sm">
                                <div className="flex items-center justify-between text-gray-500">
                                    <span>Məhsullar</span>
                                    <span>{formatMoney(cartSummary.subtotal)}</span>
                                </div>
                                {cartSummary.servicePercent > 0 && (
                                    <div className="flex items-center justify-between text-gray-500">
                                        <span>{cartSummary.servicePercent}% xidmət haqqı</span>
                                        <span>{formatMoney(cartSummary.serviceAmount)}</span>
                                    </div>
                                )}
                                <div className="flex items-center justify-between pt-1 text-lg font-bold text-gray-900">
                                    <span>Ümumi</span>
                                    <span>{formatMoney(cartSummary.total)}</span>
                                </div>
                            </div>

                            {submitError && (
                                <div className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
                                    {submitError}
                                </div>
                            )}

                            {orderingOn ? (
                                <button
                                    type="button"
                                    onClick={submitOrder}
                                    disabled={cartItems.length === 0 || submitting}
                                    className="qr-brand-bg mt-4 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-semibold text-white shadow-lg shadow-black/10 transition-transform active:scale-[0.99] disabled:opacity-50"
                                >
                                    <BellRing className="h-5 w-5" />
                                    {submitting ? 'Göndərilir...' : 'Sifariş ver'}
                                </button>
                            ) : (
                                <div className="mt-4 flex items-center gap-3 rounded-2xl bg-amber-50 px-4 py-3 ring-1 ring-amber-100">
                                    <ClipboardList className="h-5 w-5 flex-shrink-0 text-amber-600" />
                                    <div>
                                        <div className="text-sm font-semibold text-amber-900">Səbət hazırdır</div>
                                        <div className="text-xs text-amber-700">Ofisianta göstərərək sifariş verin</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Qeyd pəncərəsi ── */}
            {noteModal.open && (
                <div
                    className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-[2px] sm:items-center sm:p-4"
                    onClick={(e) => e.target === e.currentTarget && setNoteModal({ open: false, cartKey: null })}
                >
                    <div className="w-full max-w-md rounded-t-[28px] bg-white p-5 shadow-2xl sm:rounded-[28px]" style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}>
                        <div className="mx-auto -mt-2 mb-3 h-1.5 w-10 rounded-full bg-gray-200 sm:hidden" />
                        <h3 className="text-lg font-bold tracking-tight text-gray-900">Ofisianta qeyd</h3>
                        <p className="mt-0.5 text-xs text-gray-400">{cart[noteModal.cartKey]?.name}</p>

                        <textarea
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            rows={4}
                            autoFocus
                            placeholder="Məs: az bişsin, soğansız olsun, sous ayrıca gəlsin..."
                            className="mt-4 w-full resize-none rounded-2xl border-0 bg-gray-50 px-4 py-3 text-sm outline-none ring-1 ring-black/[0.05] transition placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-[color:var(--qr-primary,#0090bb)]"
                        />

                        <div className="mt-4 flex gap-2.5">
                            <button
                                type="button"
                                onClick={() => { setNoteModal({ open: false, cartKey: null }); setNoteDraft(''); }}
                                className="flex-1 rounded-2xl bg-gray-100 px-4 py-3.5 text-sm font-semibold text-gray-700"
                            >
                                Ləğv et
                            </button>
                            <button
                                type="button"
                                onClick={saveNote}
                                className="qr-brand-bg flex-1 rounded-2xl px-4 py-3.5 text-sm font-semibold text-white"
                            >
                                Yadda saxla
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Variant seçimi ── */}
            {variantModal.open && variantModal.item && (
                <div
                    className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-[2px] sm:items-center sm:p-4"
                    onClick={(e) => e.target === e.currentTarget && setVariantModal({ open: false, item: null })}
                >
                    <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:rounded-[28px]">
                        <div className="mx-auto mt-2.5 h-1.5 w-10 flex-shrink-0 rounded-full bg-gray-200 sm:hidden" />
                        <div className="flex flex-shrink-0 items-center gap-3 px-5 pb-3 pt-3">
                            {variantModal.item.image_url && (
                                <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-xl bg-gray-100">
                                    <img src={toAssetUrl(variantModal.item.image_url)} alt="" draggable={false} className="h-full w-full object-cover" />
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <h3 className="truncate text-lg font-bold tracking-tight text-gray-900">{variantModal.item.name}</h3>
                                <p className="text-xs text-gray-400">Seçim edin</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setVariantModal({ open: false, item: null })}
                                aria-label="Bağla"
                                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
                            {(variantModal.item.variants || []).map((variant) => {
                                const vKey = makeCartKey(variantModal.item.id, variant.id);
                                const vQty = Number(cart[vKey]?.qty || 0);
                                return (
                                    <div
                                        key={variant.id}
                                        className={`flex items-center justify-between gap-3 rounded-2xl px-4 py-3 ring-1 transition-colors ${vQty > 0 ? 'qr-soft ring-black/[0.06]' : 'bg-gray-50 ring-black/[0.03]'}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-gray-900">{variant.name}</div>
                                            <div className="qr-brand-text mt-0.5 text-sm font-bold">{formatMoney(variant.price)}</div>
                                        </div>

                                        {vQty > 0 ? (
                                            <div className="flex h-9 flex-shrink-0 items-center rounded-full bg-white shadow-sm ring-1 ring-black/[0.06]">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const step = getStepBySaleType(variantModal.item);
                                                        updateCartQty(vKey, variantModal.item, vQty - step);
                                                    }}
                                                    aria-label="Azalt"
                                                    className="qr-brand-text flex h-9 w-9 items-center justify-center rounded-full active:scale-90"
                                                >
                                                    <Minus className="h-4 w-4" strokeWidth={2.5} />
                                                </button>
                                                <span className="min-w-[22px] text-center text-sm font-bold text-gray-900">
                                                    {formatQty(vQty, variantModal.item.is_set ? 'piece' : variantModal.item.sale_type)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => addToCart(variantModal.item, variant)}
                                                    aria-label="Artır"
                                                    className="qr-brand-text flex h-9 w-9 items-center justify-center rounded-full active:scale-90"
                                                >
                                                    <Plus className="h-4 w-4" strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => addToCart(variantModal.item, variant)}
                                                aria-label="Əlavə et"
                                                className="qr-brand-bg flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-white shadow-sm active:scale-90"
                                            >
                                                <Plus className="h-5 w-5" strokeWidth={2.5} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                onClick={() => setVariantModal({ open: false, item: null })}
                                className="mt-2 w-full rounded-2xl bg-gray-900 py-3.5 text-sm font-semibold text-white"
                            >
                                Hazırdır
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
};

// Mövsümi bəzək qatı bütün ekranları (giriş, V1, V2, uğur) əhatə etsin deyə üst qatda göstərilir
const QrMenu = () => {
    const [season, setSeason] = useState('');
    return (
        <>
            <QrMenuInner onSeason={setSeason} />
            {season && <SeasonalLayer season={season} />}
        </>
    );
};

export default QrMenu;
